import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import { useEffect, useRef, useState, type ElementType } from "react";
import { useTranslation } from "react-i18next";
import { LoginResponse } from "subModule/src/services/userProfile/interface";
import { EnqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { Controller, useForm } from "react-hook-form";
import DynamicSelect from "common/Select/Select";
import { GetFleetAvailabilityResponse } from "subModule/src/services/analytics/interface";
import { useGetFleetAvailabilityService } from "subModule/src/services/analytics/useAnalytics";
import {
  useGetAccountsDropdownService,
  useGetClientsDropdownService,
} from "subModule/src/services/client/useClient";
import { InfiniteData } from "@tanstack/react-query";
import { GetStationDropdownResponse } from "subModule/src/services/station/interface";
import {
  dashboardWidgetHeaderSx,
  dashboardWidgetFiltersSx,
  dashboardWidgetTitleSx,
  dashboardWidgetIconSx,
  dashboardWidgetTitleRowSx,
} from "components/Analytics/dashboardWidgetLayout";

interface FleetAvailabilityProps {
  user: LoginResponse | undefined;
  themes: any;
  stations: { id: number; name: string }[] | [];
  enqueueSnackbar: EnqueueSnackbar;
  stationsLoaded: boolean;
  handleStationActions: (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => void;
  setPageNo: any;
  stationsData: InfiniteData<GetStationDropdownResponse, unknown> | undefined;
  searchLoading: string | null;
  pageNo: number;
  dropdownSearchTerm: any;
  setDropdownSearchTerm: any;
  globalFilters: { stationId: any; range: any; allStation: boolean };
  globalFilterApplied: boolean;
  setGlobalFilterApplied: (value: boolean) => void;
}

const compactSelectSx = {
  "& .css-7896aj-control, & .css-1xiok1u-control": {
    minHeight: "auto",
    fontSize: '9px',
    height: "auto",
    overflow: "hidden",
    "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
      {
        background: "#fff",
        padding: 0,
        width: "15px",
        height: "15px",
      },
    "& .css-pogzpp-loadingIndicator, & .css-o9ehiq-loadingIndicator": {
      position: "absolute",
      right: 0,
    },
    border: 0,
  },
};

const formatCount = (value: number) =>
  new Intl.NumberFormat("en", {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 2,
  }).format(value || 0);

const formatPct = (value: number) =>
  `${new Intl.NumberFormat("en", {
    maximumFractionDigits: 1,
  }).format(value || 0)}%`;

function FleetProgressBar({
  title,
  subtitle,
  value,
  percent,
  color,
  hint,
  Icon,
}: {
  title: string;
  subtitle: string;
  value: number;
  percent: number;
  color: string;
  hint?: string;
  Icon: ElementType;
}) {
  const { t } = useTranslation();
  const pct = Math.min(Math.max(percent, 0), 100);
  const showInnerLabel = pct >= 22;

  return (
    <Box>
      <Box sx={{ ...dashboardWidgetHeaderSx, mb: 1.5 }}>
        <Box sx={dashboardWidgetTitleRowSx}>
          <Box
            sx={{
              width: 18,
              height: 18,
              borderRadius: "5px",
              background: `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)`,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon sx={{ fontSize: 11, color: "#FFFFFF" }} />
          </Box>
          <Typography
            className="font-Saira"
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: "#0F2744",
              lineHeight: 1.3,
            }}
          >
            {title}{" "}
            <Typography
              component="span"
              className="font-Saira"
              sx={{ fontSize: 11, fontWeight: 500, color: "#9CA3AF" }}
            >
              ({subtitle})
            </Typography>
          </Typography>
        </Box>
        <Box className="flex items-baseline gap-1 flex-shrink-0">
          <Typography
            className="font-Saira"
            sx={{ fontSize: 11, color: "#9CA3AF", fontWeight: 500 }}
          >
            {formatPct(percent)}
          </Typography>
          <Tooltip title={value.toLocaleString()} enterDelay={0}>
            <Typography
              className="font-Saira"
              sx={{ fontSize: 17, fontWeight: 800, color, lineHeight: 1 }}
            >
              {formatCount(value)}
            </Typography>
          </Tooltip>
          <Typography
            className="font-Saira"
            sx={{ fontSize: 10, color: "#9CA3AF", fontWeight: 500 }}
          >
            {t("units").toLowerCase()}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          position: "relative",
          height: 28,
          borderRadius: 999,
          backgroundColor: "#EEF0F5",
          overflow: "hidden",
        }}
      >
        {hint ? (
          <Typography
            className="font-Saira"
            sx={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: 9,
              color: "#9CA3AF",
              fontWeight: 500,
              whiteSpace: "nowrap",
              zIndex: 0,
              maxWidth: "46%",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {hint}
          </Typography>
        ) : null}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            height: "100%",
            width: `${pct}%`,
            minWidth: pct > 0 ? 28 : 0,
            borderRadius: 999,
            background: `linear-gradient(90deg, ${color} 0%, ${color}B8 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: showInnerLabel ? "flex-end" : "center",
            px: showInnerLabel ? 1.25 : 0,
            transition: "width 0.35s ease",
          }}
        >
          {showInnerLabel ? (
            <Typography
              className="font-Saira"
              sx={{
                fontSize: 10,
                fontWeight: 700,
                color: "#FFFFFF",
                letterSpacing: "0.04em",
                whiteSpace: "nowrap",
              }}
            >
              {formatCount(value)} {t("units").toUpperCase()}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}

export default function FleetAvailability(props: FleetAvailabilityProps) {
  const { t } = useTranslation();
  const firstRender = useRef(true);
  const screenWidth = useMediaQuery("(max-width: 680px)");
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState<any>("");
  const [page, setPage] = useState(1);
  const [dropdownLoading, setDropdownLoading] = useState<string | null>(null);
  const debouncingTimer = useRef<NodeJS.Timeout | null>(null);
  const {
    control,
    formState: { errors },
    setValue,
    watch,
    getValues,
  } = useForm<{ [key: string]: any }>({
    defaultValues: {
      range: "all",
      allStation: false,
    },
  });
  const values = watch(["stationId", "customerId", "accountId", "allStation"]);
  const primary = props.themes?.primary || "#7C3AED";
  const secondary = props.themes?.secondary || "#F97316";
  const filtersOnNextLine =
    screenWidth || Boolean(watch("customerType")?.value);

  const { refetch: apiRefetch } = useGetFleetAvailabilityService({
    stationId: values[0]?.value || null,
    range: getValues("range"),
    customerId: values[1]?.value,
    accountId: values[2]?.value,
    allStation: values[3],
  });
  const { data: clientsData, refetch: clientsRefetch } =
    useGetClientsDropdownService({
      pageNumber: page,
      size: 50,
      type: [170],
      search: searchTerm,
    });
  const { data: accountsData, refetch: accountsRefetch } =
    useGetAccountsDropdownService({
      pageNumber: page,
      size: 50,
      search: searchTerm,
    });
  const clients = (clientsData?.pages[0]?.data?.content || []).map(
    (item: { id?: number; name?: string }) => ({
      value: item.id,
      label: item.name,
    }),
  );
  const accounts = (accountsData?.pages[0]?.data?.content || []).map(
    (item: { id?: number; title?: string | null }) => ({
      value: item.id,
      label: item?.title || item.id,
    }),
  );

  useEffect(() => {
    if (firstRender.current && props.stationsLoaded === true) {
      firstRender.current = false;
      setValue(
        "stationId",
        (props?.stations?.length > 1
          ? props?.stations?.[1]
          : props?.stations?.[0]) || null,
      );
      setTimeout(() => fetchData(), 50);
    }
  }, [props.stations]);

  useEffect(() => {
    if (values[1]?.value || values[2]?.value) {
      setTimeout(() => fetchData(), 50);
    }
  }, [values[1]?.value, values[2]?.value]);

  useEffect(() => {
    if (props.globalFilterApplied === true) {
      setValue("stationId", props.globalFilters.stationId);
      setValue("allStation", props.globalFilters.allStation);
      setTimeout(() => fetchData(), 100);
    }
  }, [props.globalFilterApplied]);

  const fetchData = async () => {
    setLoading(true);
    await apiRefetch()
      .then((res: GetFleetAvailabilityResponse | unknown | any) => {
        if (res?.data?.success) {
          setData(res?.data?.data || {});
        } else {
          props.enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => {
        setLoading(false);
        if (props.globalFilterApplied === true)
          props.setGlobalFilterApplied(false);
      });
  };

  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setPage(1);
      setSearchTerm(search);
      if (search) setDropdownLoading(typeName);
    } else {
      setPage(page + 1);
      setDropdownLoading(typeName);
    }
    if (debouncingTimer.current) clearTimeout(debouncingTimer.current);
    debouncingTimer.current = setTimeout(() => {
      if (typeName === "accounts")
        accountsRefetch().finally(() => setDropdownLoading(null));
      else if (typeName === "customers")
        clientsRefetch().finally(() => setDropdownLoading(null));
    }, 300);
  };

  const summary = data?.all || {};
  const totalFleet = Number(summary.totalFleet || 0);
  const inGarage = Number(summary.inGarageFleet || 0);
  const available = Number(summary.availableFleet || 0);
  const inGaragePct = Number(summary.inGaragePercentage || 0);
  const availablePct = Number(summary.availabilityPercentage || 0);
  const otherUnits = Math.max(totalFleet - inGarage - available, 0);

  return (
    <Card
      sx={{
        height: "100%",
        minHeight: 250,
        boxShadow: "0 1px 3px rgba(16, 24, 40, 0.06)",
        borderRadius: "12px",
        border: "1px solid #EEF0F3",
        "& .MuiCardContent-root": {
          padding: "16px !important",
          height: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <CardContent>
        <Box
          sx={{ ...dashboardWidgetHeaderSx, flexShrink: 0, mb: 1.5 }}
        >
          <Box sx={dashboardWidgetTitleRowSx}>
            <Box
              sx={{
                ...dashboardWidgetIconSx,
                width: 36,
                height: 36,
                borderRadius: "10px",
                backgroundColor: `${primary}1F`,
                color: primary,
                
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                "& svg": { fontSize: 17, color: primary },
              }}
            >
              <LocalShippingOutlinedIcon />
            </Box>
            <Box
              sx={{
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: "2px",
              }}
            >
              <Typography
                className="font-Saira text-left"
                sx={{
                  fontSize: { xs: 12, sm: 15 },
              fontWeight: { xs: 600, sm: 700 },
              textAlign: "left",
                  color: "#0F2744",
                  lineHeight: 1.25,
                }}
              >
                {t("fleetAvailability")}
              </Typography>
              <Typography
                className="font-Saira text-left"
                sx={{
                  fontSize: 11,
                  fontWeight: 500,
                  color: "#9CA3AF",
                  lineHeight: 1.3,
                }}
              >
                {t("totalVsInGarageVsAvailable")}
              </Typography>
            </Box>
          </Box>

          <Box
            className="flex flex-wrap"
            sx={{
              flexShrink: 0,
              gap: 0.5,
              ...dashboardWidgetFiltersSx,
            }}
          >
            <Controller
              name="stationId"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box sx={compactSelectSx}>
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={props?.stations || []}
                    isDisabled={loading}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      if (selectedOption?.value === 0)
                        setValue("allStation", true);
                      else setValue("allStation", false);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.stationId?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    defaultPlaceholder={t("station")}
                    isLoading={props.searchLoading === "fleetAvailability"}
                    onInputChange={(
                      search: string,
                      { action }: { action: string },
                    ) => {
                      if (
                        action === "input-change" ||
                        (action !== "set-value" &&
                          action !== "menu-close" &&
                          action !== "input-blur")
                      )
                        props.handleStationActions(
                          "fleetAvailability",
                          "searchIt",
                          search,
                        );
                      else if (
                        action === "input-blur" ||
                        action === "menu-close"
                      ) {
                        if (props.dropdownSearchTerm || props.pageNo > 1)
                          props.handleStationActions(
                            "fleetAvailability",
                            "searchIt",
                            "",
                          );
                        else props.setDropdownSearchTerm("");
                        props.setPageNo(1);
                      }
                    }}
                    onMenuScrollToBottom={() => {
                      if (!(props.stationsData?.pages[0]?.data as any).last)
                        props.handleStationActions(
                          "fleetAvailability",
                          "loadNext",
                        );
                    }}
                    page={props.pageNo}
                  />
                </Box>
              )}
            />
            <Controller
              name="customerType"
              control={control}
              defaultValue={null}
              render={({ field }) => (
                <Box sx={compactSelectSx}>
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={[
                      { value: "Individual", label: t("Individual") },
                      { value: "company", label: t("company") },
                    ]}
                    isSearchable
                    isDisabled={loading}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      setValue("accountId", null);
                      setValue("customerId", null);
                      if (watch("customerType")?.value === "Individual")
                        clientsRefetch();
                      else if (watch("customerType")?.value === "company")
                        accountsRefetch();
                    }}
                    ddWidth="30px"
                    ddHeight="30px"
                    defaultPlaceholder={t("select") + " " + t("type")}
                    value={
                      field.value
                        ? {
                            value: field.value.value,
                            label: t(
                              field.value.value === "Individual"
                                ? "Individual"
                                : "company",
                            ),
                          }
                        : null
                    }
                  />
                </Box>
              )}
            />
            {watch("customerType")?.value === "company" ? (
              <Controller
                name="accountId"
                control={control}
                defaultValue={null}
                render={({ field }) => (
                  <Box sx={compactSelectSx}>
                    <DynamicSelect
                      label={""}
                      {...field}
                      options={accounts}
                      isSearchable
                      isDisabled={loading}
                      onChange={(selectedOption: any) => {
                        field.onChange(selectedOption);
                        setValue("customerId", null);
                        setTimeout(() => clientsRefetch(), 50);
                      }}
                      error={errors.accountId?.message as string}
                      ddWidth="30px"
                      ddHeight="30px"
                      defaultPlaceholder={t("select") + " " + t("company")}
                      isLoading={dropdownLoading === "accounts"}
                      onInputChange={(
                        search: string,
                        { action }: { action: string },
                      ) => {
                        if (
                          action === "input-change" ||
                          (action !== "set-value" &&
                            action !== "menu-close" &&
                            action !== "input-blur")
                        )
                          handleDropdownActions("accounts", "searchIt", search);
                        else if (
                          action === "input-blur" ||
                          action === "menu-close"
                        ) {
                          if (searchTerm || page > 1)
                            handleDropdownActions("accounts", "searchIt", "");
                          else setSearchTerm("");
                          setPage(1);
                        }
                      }}
                      onMenuScrollToBottom={() => {
                        if (!(accountsData?.pages[0]?.data as any).last)
                          handleDropdownActions("accounts", "loadNext");
                      }}
                      page={page}
                    />
                  </Box>
                )}
              />
            ) : null}
            {watch("customerType")?.value === "Individual" ? (
              <Controller
                name="customerId"
                control={control}
                defaultValue={null}
                render={({ field }) => (
                  <Box sx={compactSelectSx}>
                    <DynamicSelect
                      label={""}
                      {...field}
                      options={clients}
                      isSearchable
                      isDisabled={loading}
                      onChange={(selectedOption: any) => {
                        field.onChange(selectedOption);
                      }}
                      error={errors.customerId?.message as string}
                      ddWidth="30px"
                      ddHeight="30px"
                      defaultPlaceholder={
                        t("select") +
                        " " +
                        (watch("accountId")?.value
                          ? t("driver")
                          : t("customer"))
                      }
                      isLoading={dropdownLoading === "customers"}
                      onInputChange={(
                        search: string,
                        { action }: { action: string },
                      ) => {
                        if (
                          action === "input-change" ||
                          (action !== "set-value" &&
                            action !== "menu-close" &&
                            action !== "input-blur")
                        )
                          handleDropdownActions(
                            "customers",
                            "searchIt",
                            search,
                          );
                        else if (
                          action === "input-blur" ||
                          action === "menu-close"
                        ) {
                          if (searchTerm || page > 1)
                            handleDropdownActions("customers", "searchIt", "");
                          else setSearchTerm("");
                          setPage(1);
                        }
                      }}
                      onMenuScrollToBottom={() => {
                        if (!(clientsData?.pages[0]?.data as any).last)
                          handleDropdownActions("customers", "loadNext");
                      }}
                      page={page}
                    />
                  </Box>
                )}
              />
            ) : null}
          </Box>
        </Box>

        {loading ? (
          <Box className="flex flex-col md:flex-row gap-3" sx={{ flex: 1 }}>
            <Skeleton
              variant="rounded"
              height={140}
              sx={{
                backgroundColor: secondary,
                borderRadius: "12px",
                width: { xs: "100%", md: 150 },
                flexShrink: 0,
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Skeleton
                sx={{ backgroundColor: secondary, mb: 1.5, height: 56 }}
              />
              <Skeleton sx={{ backgroundColor: secondary, height: 56 }} />
            </Box>
          </Box>
        ) : !data ? (
          <Box className="flex justify-center items-center" sx={{ flex: 1 }}>
            <Typography
              className="font-Saira text-center italic font-semibold"
              sx={{ color: primary }}
            >
              {t("noRecordFound")}
            </Typography>
          </Box>
        ) : (
          <Box
            className="flex flex-col md:flex-row gap-3"
            sx={{ flex: 1, minHeight: 0, alignItems: "stretch" }}
          >
            <Box
              sx={{
                flex: { xs: "1 1 auto", md: "0 0 148px" },
                borderRadius: "12px",
                backgroundColor: "#EFF6FF",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                py: 2,
                px: 1.5,
              }}
            >
              <LocalShippingOutlinedIcon
                sx={{ fontSize: 25, color: "#2563EB" }}
              />

              <Typography
                className="font-Saira"
                sx={{ fontSize: 11, fontWeight: 600, color: "#6B7280" }}
              >
                {t("totalFleet")}
              </Typography>
              <Tooltip title={totalFleet.toLocaleString()} enterDelay={0}>
                <Typography
                  className="font-Saira"
                  sx={{
                    fontSize: 29,
                    fontWeight: 800,
                    color: "#0F2744",
                    lineHeight: 1,
                  }}
                >
                  {formatCount(totalFleet)}
                </Typography>
              </Tooltip>
            </Box>

            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: 2.25,
                minWidth: 0,
              }}
            >
              <FleetProgressBar
                title={t("inGarageFleet")}
                subtitle={t("inGarageFleetHint")}
                value={inGarage}
                percent={inGaragePct}
                color={secondary}
                Icon={BuildCircleOutlinedIcon}
              />
              <FleetProgressBar
                title={t("availableFleet")}
                subtitle={t("availableFleetHint")}
                value={available}
                percent={availablePct}
                color={primary}
                Icon={DirectionsCarFilledOutlinedIcon}
                hint={
                  otherUnits > 0
                    ? t("standbyReservedUnits", { count: otherUnits })
                    : undefined
                }
              />
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
